import type { RunVisualViewId } from "@/api/run-visuals";
import type { RunVisualAvailability, RunVisualView } from "@/components/runs/run-visual-views";

/**
 * The run workspace's MAIN-AREA MODE, and the side panel that pairs with it
 * (flowstack#991).
 *
 * The workspace used to be a fixed split: the DAG on the left, always, with a
 * 26rem panel on the right carrying whatever visual the run had produced. That
 * shape gave the graph the whole main area and left every *visual* surface in
 * a column too narrow to read — three ~90px torsion cards, or a 3D structure
 * in a box smaller than the tile-settings list beneath it.
 *
 * The operator's model (#991, second comment) replaces it: **the main area is
 * a mode, and the side panel follows the mode.** The user picks what the main
 * area shows; whatever that is gets the full width the DAG used to monopolise,
 * and the side panel is then always the DETAIL of that thing rather than a
 * generic dumping ground.
 *
 * | Main area     | Side panel                                    |
 * |---------------|-----------------------------------------------|
 * | DAG           | tile settings — as before                     |
 * | Torsion plots | the molecule, plus the selected fit's numbers |
 * | Molecule      | the fragment list and its fit numbers         |
 *
 * The last two are the same pairing inverted: structure beside numbers, with
 * whichever one you came to read taking the main area.
 *
 * Kept as pure functions over the visuals index (like `run-visual-views.ts`,
 * whose availability rules this reuses rather than restates) so the mode
 * table is testable without mounting reactflow, a chart or a 3D viewer.
 */

/** The main area's mode. `dag` plus every view a run can visualize. */
export type RunWorkspaceModeId = "dag" | RunVisualViewId;

/** What the side panel shows for a given mode. */
export type RunWorkspaceSideId = "tile" | "molecule" | "fragments";

export interface RunWorkspaceMode {
  id: RunWorkspaceModeId;
  label: string;
  availability: RunVisualAvailability;
  /** The visual view backing this mode; `null` for the DAG. */
  view: RunVisualView | null;
}

/**
 * DAG first, then the visual views in their existing order.
 *
 * Spliced onto `RUN_VISUAL_VIEW_ORDER` rather than re-listed, so the visual
 * modes cannot drift out of step with the order the rest of the app uses.
 */
const DAG_LABEL = "DAG";

/**
 * Build the mode list.
 *
 * The DAG is unconditionally `ready`: this workspace only renders for a run
 * that HAS a flow graph (`RunWorkspace` bails to an empty state otherwise), so
 * there is no state in which the graph is pending or absent. Every other mode
 * inherits its availability from `deriveRunVisualViews` — the same three-state
 * ready/pending/absent rule the old selector used, unchanged: a mode whose data
 * has not arrived yet is offered-but-disabled and says *pending*, and once the
 * run is terminal it says the run produced none. Neither is ever silently
 * hidden, because a hidden mode reads as "this portal cannot do that".
 */
export function deriveWorkspaceModes(views: readonly RunVisualView[]): RunWorkspaceMode[] {
  return [
    { id: "dag" as const, label: DAG_LABEL, availability: "ready" as const, view: null },
    ...views.map((view) => ({
      id: view.id,
      label: view.label,
      availability: view.availability,
      view,
    })),
  ];
}

/**
 * The mode to show, given the user's pick.
 *
 * Falls back to the DAG rather than to "the first ready mode": the DAG is the
 * only mode guaranteed to have something to draw, and it is what the tab
 * showed before a user picked anything. A run whose torsion plots were
 * superseded by a resume therefore lands back on the graph, never on a mode
 * whose data has gone.
 */
export function resolveWorkspaceMode(
  modes: readonly RunWorkspaceMode[],
  picked: RunWorkspaceModeId | null
): RunWorkspaceMode {
  const chosen = modes.find((m) => m.id === picked && m.availability === "ready");
  // By ID, not `modes[0]` (Rupert PR-999 r1): the fallback IS "the DAG", and
  // spelling it as a position makes that true only for as long as
  // `deriveWorkspaceModes` happens to emit the DAG first. A reorder — or a
  // caller assembling its own `modes` array — would silently turn the
  // guarantee into "whatever is first" with nothing to catch it.
  return chosen ?? modes.find((m) => m.id === "dag") ?? modes[0];
}

/**
 * The side panel that pairs with a mode — the whole of #991's "side panel
 * dependent".
 *
 * Exhaustive over `RunWorkspaceModeId` by construction (a `Record`, not a
 * switch with a default), so adding a mode without deciding its side panel is
 * a type error rather than a silent fallback to the tile settings.
 */
const SIDE_BY_MODE: Record<RunWorkspaceModeId, RunWorkspaceSideId> = {
  dag: "tile",
  // Reading a fit: the numbers are the main event, the structure is the
  // reference beside them.
  torsion: "molecule",
  // ...and inverted. Reading a structure, the fit numbers are the reference.
  molecule: "fragments",
};

export function sidePanelFor(mode: RunWorkspaceModeId): RunWorkspaceSideId {
  return SIDE_BY_MODE[mode];
}

/** Heading for the side panel — says what it is showing, never "detail". */
const SIDE_HEADINGS: Record<RunWorkspaceSideId, string> = {
  tile: "Tile settings",
  molecule: "Molecule",
  fragments: "Torsion fits",
};

export function sideHeadingFor(side: RunWorkspaceSideId): string {
  return SIDE_HEADINGS[side];
}
